import React, { useEffect, useMemo, useState } from 'react';
/**
 * WordBuilderRenderer — SPR specialist full-word producer (dictation-grade).
 *
 * The full-word-production sister of SpellingPickRenderer. Where spelling_pick
 * is a binary grapheme PICK and verb_ending is an ENDING pick, word_builder is
 * maximal production with ZERO recognition: the learner builds the ENTIRE word
 * from a shuffled letter bank (tap-to-place), no answer options, no grapheme
 * choice. A short `clue` says WHICH word to spell without revealing its
 * spelling. The most dictatish step for the recall-heavy SPR weet-families.
 *
 * Contract (QuestionRendererProps):
 *  - reports ready via onReadyChange once every item has a non-empty built word
 *  - emits the answer (JSON array of built strings, item-aligned) on the
 *    rising edge of shouldValidate
 */
import { motion, AnimatePresence } from 'framer-motion';
import { QuestionRendererProps } from './types';
import { FALLBACK_COLOR, hexToRgba } from './rendererUtils';
import { useEdgeTriggeredSubmit } from './useEdgeTriggeredSubmit';
import { Delete as DeleteIcon, RotateCcw as RotateCcwIcon } from 'lucide-react';
import { resolveWordBuilderLetterBank, type WordBuilderConfig } from '../../../content/types';
import { shuffleWithSeed } from '../../../utils/deterministicShuffle';
import { soundManager } from '../../../utils/soundManager';
import { hapticManager } from '../../../utils/hapticManager';
const VARIANT_LABEL: Record<WordBuilderConfig['variant'], string> = {
  'ei-ij': 'Bouw het woord (ei of ij?)',
  'd-t': 'Bouw het woord (d of t?)',
  dt: 'Bouw het woord (let op de -dt)',
  custom: 'Bouw het hele woord'
};
/** One tappable bank tile: a letter with a stable id so duplicates are distinct. */
interface BankTile {
  id: string;
  letter: string;
}
export function WordBuilderRenderer({
  question,
  onAnswer,
  onReadyChange,
  shouldValidate,
  moduleColor
}: QuestionRendererProps) {
  const color = moduleColor || FALLBACK_COLOR;
  const config = question.data?.wordBuilder as WordBuilderConfig | undefined;
  const items = config?.items ?? [];
  // Per-item shuffled letter bank, deterministic by question.id + item index.
  const banksPerItem = useMemo<BankTile[][]>(() => {
    if (!config) return [];
    return items.map((item, ii) => {
      const letters = resolveWordBuilderLetterBank(item, config);
      const {
        shuffled
      } = shuffleWithSeed(letters, `${question.id}-wb${ii}`);
      return shuffled.map((letter, li) => ({
        id: `${ii}-${li}-${letter}`,
        letter
      }));
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [config, question.id]);
  // placedPerItem[itemIndex] = ordered list of bank tile ids the learner placed.
  const [placedPerItem, setPlacedPerItem] = useState<string[][]>(() => items.map(() => []));
  // Reset when the question changes.
  useEffect(() => {
    setPlacedPerItem(items.map(() => []));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [question.id]);
  // Built string per item, derived from the placed tile ids.
  const builtPerItem = useMemo(() => banksPerItem.map((bank, ii) => {
    const byId = new Map(bank.map(t => [t.id, t.letter]));
    return (placedPerItem[ii] ?? []).map(id => byId.get(id) ?? '').join('');
  }), [banksPerItem, placedPerItem]);
  const allFilled = items.length > 0 && builtPerItem.every(w => w.length > 0);
  useEffect(() => {
    onReadyChange?.(allFilled);
  }, [allFilled, onReadyChange]);
  // Edge-triggered submit — payload is an item-aligned JSON array of built words.
  const answerPayload = useMemo(() => JSON.stringify(builtPerItem), [builtPerItem]);
  useEdgeTriggeredSubmit(answerPayload, shouldValidate, onAnswer, question.id);
  const placeLetter = (itemIndex: number, tileId: string) => {
    setPlacedPerItem(prev => {
      const next = prev.map(arr => [...arr]);
      if (next[itemIndex].includes(tileId)) return prev;
      next[itemIndex].push(tileId);
      return next;
    });
    soundManager.playClick();
    hapticManager.hapticSelection();
  };
  const backspace = (itemIndex: number) => {
    setPlacedPerItem(prev => {
      if ((prev[itemIndex] ?? []).length === 0) return prev;
      const next = prev.map(arr => [...arr]);
      next[itemIndex].pop();
      return next;
    });
    hapticManager.hapticSelection();
  };
  const clearItem = (itemIndex: number) => {
    setPlacedPerItem(prev => {
      if ((prev[itemIndex] ?? []).length === 0) return prev;
      const next = prev.map(arr => [...arr]);
      next[itemIndex] = [];
      return next;
    });
    hapticManager.hapticSelection();
  };
  if (!config || items.length === 0 || banksPerItem.length === 0) {
    return <div className="p-4 bg-amber-50 rounded-xl text-amber-800 font-semibold text-center" data-id="element-11404">
        Deze vraag mist gegevens om te tonen.
      </div>;
  }
  return <motion.div initial={{
    opacity: 0,
    y: 10
  }} animate={{
    opacity: 1,
    y: 0
  }} transition={{
    duration: 0.3
  }} className="w-full max-w-xl mx-auto" data-id="element-11405">
      {/* Variant chip — instant "what am I doing" cue (P6). */}
      <div className="flex justify-center mb-5" data-id="element-11406">
        <span className="inline-flex items-center px-4 py-1.5 rounded-full text-sm font-bold tracking-wide" style={{
        backgroundColor: hexToRgba(color, 0.12),
        color
      }} data-id="element-11407">
          {config.promptLabel || VARIANT_LABEL[config.variant]}
        </span>
      </div>

      <ul className="flex flex-col gap-4" role="list" data-id="element-11408">
        {items.map((item, ii) => {
        const placed = placedPerItem[ii] ?? [];
        const bank = banksPerItem[ii];
        const byId = new Map(bank.map(t => [t.id, t.letter]));
        const usedIds = new Set(placed);
        const hasAny = placed.length > 0;
        return <li key={`${question.id}-wb${ii}`} className="flex flex-col gap-3 bg-white border-2 border-slate-200 rounded-2xl px-4 py-4 shadow-sm" data-id="element-11409">
              {/* Clue line — says WHICH word, without its spelling. */}
              <p className="text-slate-500 text-base sm:text-lg font-semibold text-center sm:text-left" data-id="element-11410">
                {item.clue}
              </p>

              {/* Built-word slot row — fills left→right as letters are tapped. */}
              <div className="flex items-center justify-center gap-1.5 min-h-[3rem] flex-wrap" role="group" aria-label={`Gebouwd woord ${ii + 1}`} aria-live="polite" data-id="element-11411">
                {hasAny ? <AnimatePresence initial={false} data-id="element-11412">
                    {placed.map((tileId, slotIndex) => <motion.span key={tileId} initial={{
                scale: 0.5,
                opacity: 0,
                y: -8
              }} animate={{
                scale: 1,
                opacity: 1,
                y: 0
              }} exit={{
                scale: 0.5,
                opacity: 0
              }} transition={{
                type: 'spring',
                stiffness: 420,
                damping: 24
              }} className="inline-flex items-center justify-center w-9 h-11 sm:w-10 sm:h-12 rounded-lg text-xl sm:text-2xl font-extrabold" style={{
                color,
                backgroundColor: hexToRgba(color, 0.1),
                border: `2px solid ${hexToRgba(color, 0.35)}`
              }} aria-label={`Letter ${slotIndex + 1}: ${byId.get(tileId)}`} data-id="element-11413">
                        {byId.get(tileId)}
                      </motion.span>)}
                  </AnimatePresence> : <span className="text-slate-300 text-2xl font-extrabold tracking-[0.3em] select-none" data-id="element-11414">
                    ____
                  </span>}
              </div>

              {/* Letter bank — tappable tiles; placed ones dim and disable. */}
              <div className="flex items-center justify-center gap-2 flex-wrap" role="group" aria-label={`Letterbak voor woord ${ii + 1}`} data-id="element-11415">
                {bank.map(tile => {
              const used = usedIds.has(tile.id);
              return <motion.button key={tile.id} type="button" onClick={() => placeLetter(ii, tile.id)} disabled={used} whileTap={used ? undefined : {
                scale: 0.9
              }} whileHover={used ? undefined : {
                scale: 1.05
              }} aria-label={`Plaats letter ${tile.letter}`} className="min-w-[2.75rem] px-3 py-2 rounded-xl text-xl font-extrabold border-2 transition-colors disabled:cursor-not-allowed" style={used ? {
                backgroundColor: '#f1f5f9',
                borderColor: '#e2e8f0',
                color: '#cbd5e1'
              } : {
                backgroundColor: '#fff',
                borderColor: '#e2e8f0',
                color: '#334155'
              }} data-id="element-11416">
                      {tile.letter}
                    </motion.button>;
            })}
              </div>

              {/* Controls — backspace + clear (no typing). */}
              <div className="flex items-center justify-center sm:justify-end gap-2" data-id="element-11417">
                <button type="button" onClick={() => backspace(ii)} disabled={!hasAny} className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-sm font-bold text-slate-600 bg-slate-100 hover:bg-slate-200 transition-colors disabled:opacity-40 disabled:cursor-not-allowed" aria-label="Laatste letter wissen" data-id="element-11418">
                  <DeleteIcon className="w-4 h-4" aria-hidden="true" data-id="element-11419" />
                  Terug
                </button>
                <button type="button" onClick={() => clearItem(ii)} disabled={!hasAny} className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-sm font-bold text-slate-600 bg-slate-100 hover:bg-slate-200 transition-colors disabled:opacity-40 disabled:cursor-not-allowed" aria-label="Hele woord wissen" data-id="element-11420">
                  <RotateCcwIcon className="w-4 h-4" aria-hidden="true" data-id="element-11421" />
                  Wissen
                </button>
              </div>
            </li>;
      })}
      </ul>

      <p className="text-center text-slate-400 text-sm mt-6 font-medium" data-id="element-11422">
        {allFilled ? 'Klaar? Klik op Controleren!' : items.length > 1 ? 'Bouw elk woord met de letters' : 'Bouw het woord met de letters'}
      </p>
    </motion.div>;
}
WordBuilderRenderer.displayName = 'WordBuilderRenderer';